import React, { useCallback, useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Button,
  CircularProgress,
  Fab,
  IconButton,
  Paper,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import SmartToyOutlinedIcon from "@mui/icons-material/SmartToyOutlined";
import CloseIcon from "@mui/icons-material/Close";
import OpenInFullIcon from "@mui/icons-material/OpenInFull";
import CloseFullscreenIcon from "@mui/icons-material/CloseFullscreen";
import SendIcon from "@mui/icons-material/Send";
import HistoryIcon from "@mui/icons-material/History";
import AddCommentOutlinedIcon from "@mui/icons-material/AddCommentOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import axiosInstance from "../../helpers/axios";
import { COLORS } from "../../constants/theme";

// memberRequest keeps these calls on the normal API even inside the reviewer admin portal.
const api = { memberRequest: true, skipSessionRedirect: true };
const errorText = (error) =>
  error.response?.data?.error || "Something went wrong. Please try again.";

// Minimal markdown for assistant replies: **bold**, `code`, "- " / "1. " lists. Builds React nodes only.
const renderInline = (text, keyPrefix) =>
  text.split(/(\*\*[^*]+\*\*|`[^`]+`)/g).filter(Boolean).map((part, index) => {
    const key = `${keyPrefix}-${index}`;
    if (part.startsWith("**") && part.endsWith("**") && part.length > 4) {
      return <strong key={key} style={{ fontWeight: 700 }}>{part.slice(2, -2)}</strong>;
    }
    if (part.startsWith("`") && part.endsWith("`") && part.length > 2) {
      return <code key={key}>{part.slice(1, -1)}</code>;
    }
    return part;
  });

const splitRow = (line) =>
  line.trim().replace(/^\|/, "").replace(/\|$/, "").split("|").map((cell) => cell.trim());
const isSeparatorRow = (line) => /^\s*\|?\s*:?-{2,}:?\s*(\|\s*:?-{2,}:?\s*)*\|?\s*$/.test(line);

const cellStyle = { border: "1px solid #d4d4d4", padding: "4px 8px", verticalAlign: "top", fontWeight: 400 };
const headStyle = { ...cellStyle, background: "#e4e4e4", fontWeight: 700, whiteSpace: "nowrap", textAlign: "left" };

// Block-level markdown: tables, headings, dividers, lists, paragraphs.
const renderMarkdown = (text) => {
  const lines = (text || "").split("\n");
  const nodes = [];
  let i = 0;
  while (i < lines.length) {
    const line = lines[i];
    if (line.includes("|") && i + 1 < lines.length && isSeparatorRow(lines[i + 1])) {
      const header = splitRow(line);
      const rows = [];
      i += 2;
      while (i < lines.length && lines[i].trim() && lines[i].includes("|")) {
        rows.push(splitRow(lines[i]));
        i += 1;
      }
      nodes.push(
        <Box key={`t${i}`} sx={{ overflowX: "auto", my: 0.75 }}>
          <table style={{ borderCollapse: "collapse", fontSize: 12, background: "#fff", width: "100%" }}>
            <thead>
              <tr>{header.map((cell, c) => <th key={c} style={headStyle}>{renderInline(cell, `h${c}`)}</th>)}</tr>
            </thead>
            <tbody>
              {rows.map((row, r) => (
                <tr key={r}>
                  {header.map((_, c) => <td key={c} style={cellStyle}>{renderInline(row[c] || "", `${r}-${c}`)}</td>)}
                </tr>
              ))}
            </tbody>
          </table>
        </Box>
      );
      continue;
    }
    const heading = line.match(/^\s*(#{1,4})\s+(.*)$/);
    const item = line.match(/^\s*(?:[-*•]|\d+[.)])\s+(.*)$/);
    if (heading) {
      nodes.push(
        <div key={i} style={{ fontWeight: 700, fontSize: heading[1].length <= 2 ? 14 : 13, margin: "8px 0 2px" }}>
          {renderInline(heading[2], i)}
        </div>
      );
    } else if (/^\s*(-{3,}|\*{3,}|_{3,})\s*$/.test(line)) {
      nodes.push(<hr key={i} style={{ border: 0, borderTop: "1px solid #d4d4d4", margin: "6px 0" }} />);
    } else if (item) {
      nodes.push(
        <Box key={i} sx={{ display: "flex", gap: 0.75, pl: 0.5 }}>
          <span>•</span>
          <span>{renderInline(item[1], i)}</span>
        </Box>
      );
    } else if (line.trim()) {
      nodes.push(<div key={i}>{renderInline(line, i)}</div>);
    } else {
      nodes.push(<div key={i} style={{ height: 6 }} />);
    }
    i += 1;
  }
  return nodes;
};

const AiAgentChat = () => {
  const user = useSelector((state) => state.auth.user);
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [wide, setWide] = useState(false);
  const [chats, setChats] = useState([]);
  const [chatId, setChatId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [enabled, setEnabled] = useState(false);
  const bottomRef = useRef(null);

  // The server decides; re-check on focus so toggling access takes effect without a re-login.
  useEffect(() => {
    if (user?.role !== "employee") { setEnabled(false); return undefined; }
    let active = true;
    const check = async () => {
      try {
        const { data } = await axiosInstance.get("/ai-agent/access", api);
        if (active) setEnabled(data.enabled === true);
      } catch {
        if (active) setEnabled(false);
      }
    };
    check();
    window.addEventListener("focus", check);
    return () => { active = false; window.removeEventListener("focus", check); };
  }, [user?._id, user?.role]);

  const loadChats = useCallback(async () => {
    try {
      const { data } = await axiosInstance.get("/ai-agent/chats", api);
      setChats(data.chats);
    } catch (err) {
      setError(errorText(err));
    }
  }, []);

  useEffect(() => {
    if (open && showHistory) loadChats();
  }, [open, showHistory, loadChats]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, busy, open]);

  if (user?.role !== "employee" || !enabled) return null;

  const applyChat = (data) => {
    setChatId(data.id);
    setMessages(data.messages);
  };

  const openChat = async (id) => {
    setError("");
    try {
      const { data } = await axiosInstance.get(`/ai-agent/chats/${id}`, api);
      applyChat(data);
      setShowHistory(false);
    } catch (err) {
      setError(errorText(err));
    }
  };

  const newChat = () => {
    setChatId(null);
    setMessages([]);
    setError("");
    setShowHistory(false);
  };

  const removeChat = async (id) => {
    try {
      await axiosInstance.delete(`/ai-agent/chats/${id}`, api);
      if (id === chatId) newChat();
      setShowHistory(true);
      loadChats();
    } catch (err) {
      setError(errorText(err));
    }
  };

  const send = async () => {
    const text = input.trim();
    if (!text || busy) return;
    setInput("");
    setError("");
    setBusy(true);
    setMessages((current) => [...current, { id: `local-${Date.now()}`, role: "user", content: text }]);
    try {
      const { data } = await axiosInstance.post("/ai-agent/chats/message", { chatId, message: text }, api);
      applyChat(data);
    } catch (err) {
      setError(errorText(err));
    } finally {
      setBusy(false);
    }
  };

  const decide = async (messageId, decision) => {
    if (busy) return;
    setBusy(true);
    setError("");
    try {
      const { data } = await axiosInstance.post(`/ai-agent/chats/${chatId}/confirm`, { messageId, decision }, api);
      applyChat(data);
    } catch (err) {
      setError(errorText(err));
    } finally {
      setBusy(false);
    }
  };

  const openReport = (action) => {
    navigate("/admin/reports/timespent", { state: { agentPdf: action } });
    setOpen(false);
  };

  const bubble = (message) => {
    const mine = message.role === "user";
    const pending = (message.pendingActions || []).filter((a) => a.status === "pending");
    const isLatestPending = pending.length > 0;
    return (
      <Box key={message.id} sx={{ display: "flex", justifyContent: mine ? "flex-end" : "flex-start", mb: 1 }}>
        <Box
          sx={{
            maxWidth: mine ? "85%" : "100%", minWidth: 0, px: 1.5, py: 1, borderRadius: 2, whiteSpace: mine ? "pre-wrap" : "normal", wordBreak: "break-word",
            bgcolor: mine ? COLORS.primary : "#f1f1f1", color: mine ? "#fff" : "#000",
          }}
        >
          <Typography variant="body2" component="div" sx={{ color: "inherit", fontWeight: 400 }}>
            {mine ? message.content : renderMarkdown(message.content)}
          </Typography>
          {(message.pendingActions || []).map((action) => (
            <Typography key={action.id} variant="caption" sx={{ display: "block", mt: 0.5, color: "inherit", fontWeight: 400 }}>
              {action.status === "pending" ? "• " : action.status === "done" ? "✓ " : "✕ "}{action.summary}
            </Typography>
          ))}
          {isLatestPending && (
            <Box sx={{ display: "flex", gap: 1, mt: 1 }}>
              <Button size="small" variant="contained" disabled={busy} onClick={() => decide(message.id, "confirm")}>
                Confirm
              </Button>
              <Button size="small" variant="outlined" disabled={busy} onClick={() => decide(message.id, "cancel")}>
                Cancel
              </Button>
            </Box>
          )}
          {(message.clientActions || []).map((action, index) => (
            <Button
              key={index} size="small" variant="contained" sx={{ mt: 1 }} onClick={() => openReport(action)}
            >
              {action.kind === "master" ? "Open master PDF" : `Open Time Spent PDF${action.matterName ? ` · ${action.matterName}` : ""}`}
            </Button>
          ))}
        </Box>
      </Box>
    );
  };

  return (
    <>
      {!open && (
        <Tooltip title="AI assistant">
          <Fab
            color="primary" aria-label="Open AI assistant" onClick={() => setOpen(true)}
            sx={{ position: "fixed", right: 20, bottom: 20, zIndex: 1300 }}
          >
            <SmartToyOutlinedIcon />
          </Fab>
        </Tooltip>
      )}
      {open && (
        <Paper
          elevation={8}
          sx={{
            position: "fixed", right: { xs: 0, sm: 20 }, bottom: { xs: 0, sm: 20 }, zIndex: 1300,
            width: { xs: "100%", sm: wide ? 860 : 480 }, maxWidth: { sm: "calc(100vw - 40px)" },
            height: { xs: "100%", sm: wide ? "85vh" : 580 }, maxHeight: "100vh",
            display: "flex", flexDirection: "column", overflow: "hidden", borderRadius: { xs: 0, sm: 2 },
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", px: 1.5, py: 1, bgcolor: COLORS.primary, color: "#fff" }}>
            <Typography variant="subtitle1" sx={{ flex: 1, color: "#fff", fontWeight: 700 }}>
              AI assistant
            </Typography>
            <Tooltip title="Chat history">
              <IconButton size="small" aria-label="Chat history" sx={{ color: "#fff" }} onClick={() => setShowHistory((v) => !v)}>
                <HistoryIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="New chat">
              <IconButton size="small" aria-label="New chat" sx={{ color: "#fff" }} onClick={newChat}>
                <AddCommentOutlinedIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title={wide ? "Smaller" : "Expand"}>
              <IconButton size="small" aria-label="Resize" sx={{ color: "#fff", display: { xs: "none", sm: "inline-flex" } }} onClick={() => setWide((v) => !v)}>
                {wide ? <CloseFullscreenIcon fontSize="small" /> : <OpenInFullIcon fontSize="small" />}
              </IconButton>
            </Tooltip>
            <IconButton size="small" aria-label="Close" sx={{ color: "#fff" }} onClick={() => setOpen(false)}>
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>

          {showHistory ? (
            <Box sx={{ flex: 1, overflowY: "auto", p: 1 }}>
              {!chats.length && <Typography variant="body2" sx={{ p: 1, fontWeight: 400 }}>No previous chats yet.</Typography>}
              {chats.map((chat) => (
                <Box
                  key={chat.id}
                  sx={{
                    display: "flex", alignItems: "center", p: 1, borderRadius: 1, cursor: "pointer",
                    bgcolor: chat.id === chatId ? "#f1f1f1" : "transparent", "&:hover": { bgcolor: "#f6f6f6" },
                  }}
                  onClick={() => openChat(chat.id)}
                >
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography variant="body2" noWrap sx={{ fontWeight: 400 }}>{chat.title}</Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 400 }}>
                      {new Date(chat.updatedAt).toLocaleString()}
                    </Typography>
                  </Box>
                  <IconButton
                    size="small" aria-label="Delete chat"
                    onClick={(event) => { event.stopPropagation(); removeChat(chat.id); }}
                  >
                    <DeleteOutlineIcon fontSize="small" />
                  </IconButton>
                </Box>
              ))}
            </Box>
          ) : (
            <Box sx={{ flex: 1, overflowY: "auto", p: 1.5 }}>
              {!messages.length && (
                <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 400 }}>
                  Ask me to add, edit or delete your time entries, check your calendar, leave balance or expenses, or apply for leave{user?.adminAccess ? ", review your team's entries, manage write-offs, the bin, invoices and firm events, or prepare Time Spent PDFs" : ""}.
                  For example: "Add 1.5 hours on Matter X for yesterday: drafted board resolution."
                </Typography>
              )}
              {messages.map(bubble)}
              {busy && <CircularProgress size={18} sx={{ m: 1 }} />}
              <div ref={bottomRef} />
            </Box>
          )}

          {error && (
            <Typography variant="caption" role="alert" sx={{ color: "error.main", px: 1.5, pb: 0.5, fontWeight: 400 }}>
              {error}
            </Typography>
          )}
          {!showHistory && (
            <Box sx={{ display: "flex", gap: 1, p: 1, borderTop: "1px solid #e5e5e5" }}>
              <TextField
                size="small" fullWidth multiline maxRows={4} placeholder="Type a message…" value={input}
                inputProps={{ maxLength: 2000 }}
                onChange={(event) => setInput(event.target.value)}
                onKeyDown={(event) => {
                  if (event.key === "Enter" && !event.shiftKey) { event.preventDefault(); send(); }
                }}
              />
              <IconButton color="primary" aria-label="Send" disabled={busy || !input.trim()} onClick={send}>
                <SendIcon />
              </IconButton>
            </Box>
          )}
        </Paper>
      )}
    </>
  );
};

export default AiAgentChat;
